import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { categories, sites } from '@/mocks/navigation';

export default function HeroSection() {
  const [searchQuery, setSearchQuery] = useState('');
  const [showResults, setShowResults] = useState(false);

  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const q = searchQuery.toLowerCase();
    const results: Array<{ name: string; url: string; description: string; icon: string; category: string }> = [];
    Object.entries(sites).forEach(([catSlug, siteList]) => {
      siteList.forEach((site) => {
        if (
          site.name.toLowerCase().includes(q) ||
          site.description.toLowerCase().includes(q) ||
          site.tags.some((t) => t.toLowerCase().includes(q))
        ) {
          results.push({ ...site, category: catSlug });
        }
      });
    });
    return results.slice(0, 8);
  }, [searchQuery]);

  return (
    <section className="relative w-full min-h-[520px] md:min-h-[620px] flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Warm%20minimalist%20abstract%20background%20with%20soft%20coral%20pink%20and%20cream%20gradients%2C%20gentle%20organic%20shapes%2C%20clean%20modern%20aesthetic%2C%20subtle%20geometric%20patterns%2C%20warm%20light%20tones%2C%20no%20text%2C%20smooth%20texture%2C%20dreamy%20atmosphere%2C%20editorial%20quality&width=1800&height=1200&seq=hero-strawberry-nav&orientation=landscape"
          alt="草莓导航背景"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/15 to-black/35"></div>
      </div>

      <div className="relative z-10 w-full px-4 md:px-6 flex flex-col items-center text-center">
        <div className="animate-fade-in-up">
          <div className="flex items-center justify-center gap-2.5 mb-4">
            <div className="w-12 h-12 md:w-14 md:h-14 rounded-2xl bg-primary-500/90 flex items-center justify-center backdrop-blur-sm">
              <i className="ri-heart-3-fill text-background-50 text-2xl md:text-3xl"></i>
            </div>
          </div>
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-background-50 mb-4 leading-tight">
            精选网站导航入口
          </h1>
          <p className="text-base md:text-lg text-background-200/90 max-w-2xl mx-auto mb-8 leading-relaxed">
            草莓导航精心整理海量优质网站资源，涵盖工具、娱乐、学习、购物等多个分类，
            让您快速找到所需网站，告别繁琐搜索，高效畅游互联网世界
          </p>
        </div>

        <div className="w-full max-w-2xl relative animate-fade-in-up animation-delay-200">
          <div className="flex items-center bg-background-50 rounded-2xl shadow-sm overflow-hidden border border-background-200/60">
            <div className="pl-5 pr-2 flex items-center">
              <i className="ri-search-line text-foreground-400 text-lg"></i>
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setShowResults(true);
              }}
              onFocus={() => setShowResults(true)}
              onBlur={() => setTimeout(() => setShowResults(false), 200)}
              placeholder="搜索网站、分类或关键词..."
              className="flex-1 py-4 text-sm bg-transparent outline-none text-foreground-900 placeholder:text-foreground-400"
            />
            <button
              onClick={() => setSearchQuery('')}
              className={`px-4 py-4 text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer whitespace-nowrap ${
                searchQuery ? 'opacity-100' : 'opacity-0 pointer-events-none'
              }`}
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>

          {showResults && searchResults.length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-background-50 rounded-xl border border-background-200/70 shadow-lg overflow-hidden z-20 animate-fade-in">
              {searchResults.map((result, idx) => (
                <a
                  key={idx}
                  href={result.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 px-5 py-3 hover:bg-background-100 transition-colors cursor-pointer border-b border-background-200/40 last:border-0"
                >
                  <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className={`${result.icon} text-primary-600 text-base`}></i>
                  </div>
                  <div className="flex-1 text-left min-w-0">
                    <div className="text-sm font-medium text-foreground-900 truncate">{result.name}</div>
                    <div className="text-xs text-foreground-500 truncate">{result.description}</div>
                  </div>
                  <i className="ri-arrow-right-up-line text-foreground-400 text-sm flex-shrink-0"></i>
                </a>
              ))}
            </div>
          )}

          {showResults && searchQuery.trim() && searchResults.length === 0 && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-background-50 rounded-xl border border-background-200/70 shadow-lg p-6 text-center z-20 animate-fade-in">
              <i className="ri-emotion-sad-line text-foreground-400 text-2xl mb-2 block"></i>
              <p className="text-sm text-foreground-500">未找到相关网站，请尝试其他关键词</p>
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-center gap-3 mt-8 animate-fade-in-up animation-delay-300">
          <span className="text-sm text-background-200/80">热门搜索：</span>
          {['AI工具', '在线课程', '购物', '视频', '设计'].map((tag) => (
            <button
              key={tag}
              onClick={() => {
                setSearchQuery(tag);
                setShowResults(true);
              }}
              className="px-3.5 py-1.5 rounded-full bg-background-50/15 backdrop-blur-sm text-sm text-background-100 border border-background-50/20 hover:bg-background-50/25 transition-all cursor-pointer whitespace-nowrap"
            >
              {tag}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center justify-center gap-3 md:gap-5 mt-10 animate-fade-in-up animation-delay-400">
          {categories.slice(0, 6).map((cat) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-background-50/10 backdrop-blur-sm border border-background-50/15 text-background-100 hover:bg-background-50/20 transition-all cursor-pointer group"
            >
              <div className="w-7 h-7 rounded-lg bg-background-50/20 flex items-center justify-center">
                <i className={`${cat.icon} text-background-100 text-sm`}></i>
              </div>
              <span className="text-sm font-medium whitespace-nowrap">{cat.name}</span>
              <i className="ri-arrow-right-line text-background-300 text-sm group-hover:translate-x-0.5 transition-transform"></i>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}